import { ObservableQuery } from '@apollo/client';

import {
  DocumentStatus,
  ReportDocumentDetailQuery,
  ReportDocumentDetailQueryVariables,
  useWatchLazyQuery,
} from '@anyx/common-graphql';
import { reportAppError } from '@anyx/shared-utils';

import { REPORT_DOCUMENT_DETAIL } from '../graphql';

interface DownloadDocumentProps {
  onProcessStart?: () => void;
  onSuccess?: () => void;
  onComplete?: () => void;
  onFailure?: (error?: unknown) => void;
  handleDocumentDownloadUrl: (url: string) => void;
  pollIntervalInMs?: number;
}

export function useDownloadReportDocument({
  onProcessStart,
  onSuccess,
  onComplete,
  onFailure,
  handleDocumentDownloadUrl,
  pollIntervalInMs = 5000,
}: DownloadDocumentProps) {
  const onSuccessCallback = ({
    data: queryResult,
    error,
  }: ObservableQuery.Result<ReportDocumentDetailQuery>) => {
    // FR-005: handle structured GraphQL errors returned by reportDocument resolver
    // (e.g. REPORT_EXPORT_FAILED DomainError when GCS write failed)
    if (error) {
      onFailure?.();
      return true; // Stop polling
    }

    if (!queryResult?.reportDocument) {
      return false; // Continue polling
    }

    const { id, status, url } = queryResult.reportDocument;

    if (!status) {
      return false; // Continue polling
    }

    switch (status) {
      case DocumentStatus.SUCCESS:
        if (!url) {
          reportAppError(`Encountered Success State with Empty URL for Document Id ${id}`);
          onFailure?.();
          return true;
        }

        handleDocumentDownloadUrl(url);
        onSuccess?.();
        return true;

      case DocumentStatus.FAIL:
        onFailure?.();
        return true;

      default:
        return false;
    }
  };

  const onErrorCallback = (error: unknown) => {
    onFailure?.(error);
  };

  const { listenToWatchedLazyQuery } = useWatchLazyQuery<
    ReportDocumentDetailQuery,
    ReportDocumentDetailQueryVariables
  >({
    query: REPORT_DOCUMENT_DETAIL,
    options: {
      pollInterval: pollIntervalInMs,
    },
  });

  const handleDownloadDocument = (queryVariables: ReportDocumentDetailQueryVariables) => {
    onProcessStart?.();
    listenToWatchedLazyQuery(queryVariables, onSuccessCallback, onErrorCallback, () => {
      onComplete?.();
    });
  };

  return {
    handleDownloadDocument,
  };
}
